<template>
  <div class="crossword-container">
    <!-- Selected clue text above the grid -->
    <div class="selected-clue" v-if="selectedClueText">
      {{ selectedClueText }}
    </div>

    <!-- Render the grid using CSS Grid -->
    <div class="grid" :style="{ '--grid-cols': localGrid[0].length }">
      <div v-for="(row, rowIndex) in localGrid" :key="rowIndex" class="row">
        <div
          v-for="(cell, colIndex) in row"
          :key="colIndex"
          class="cell"
          :class="[
            { 'cell-block': !cell || cell.isBlack },
            { active: isActiveCell(rowIndex, colIndex) },
            { solved: cell && cell.solved }
          ]"
          @click="handleCellClick(rowIndex, colIndex)"
        >
          <!-- Render white cells only -->
          <template v-if="cell && !cell.isBlack">
            <div v-if="cell.number" class="cell-number">{{ cell.number }}</div>
            <input
              :id="`cell-${rowIndex}-${colIndex}`"
              type="text"
              maxlength="1"
              v-model="cell.userValue"
              @input="handleInput(rowIndex, colIndex, $event)"
              @keydown="handleKeyDown(rowIndex, colIndex, $event)"
              :disabled="disabled"
            />
          </template>
        </div>
      </div>
    </div>

    <!-- Selected clue text below the grid -->
    <div class="selected-clue" v-if="selectedClueText">
      {{ selectedClueText }}
    </div>

    <button @click="checkAnswers">Check Answers</button>
    <div v-if="resultMessage" class="result-message">
      {{ resultMessage }}
    </div>
  </div>
</template>

<script>
export default {
  name: "CrosswordPlugin",
  props: {
    grid: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      default: "Crossword Puzzle",
    },
    clues: {
      type: Object,
      default: () => ({ across: [], down: [] }),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      resultMessage: "",
      localGrid: [],
      activeCells: [],
      selectedClue: null,
    };
  },
  computed: {
    selectedClueText() {
      if (!this.selectedClue) return "";
      const orientation = this.selectedClue.orientation;
      const clueNumber = this.selectedClue.number;
      const clueArray = this.clues[orientation] || [];
      const found = clueArray.find(clue => clue.number === clueNumber);
      return found ? `${found.number}. ${found.text}` : "";
    },
  },
  methods: {
    checkAnswers() {
      this.updateSolvedStatus();
      this.resultMessage =
        "Checked answers. Correctly solved clues are now highlighted in pastel green.";
    },
    updateSolvedStatus() {
      for (let i = 0; i < this.localGrid.length; i++) {
        for (let j = 0; j < this.localGrid[i].length; j++) {
          if (this.localGrid[i][j] && !this.localGrid[i][j].isBlack) {
            this.localGrid[i][j].solved = false;
          }
        }
      }
      let solved = { across: new Set(), down: new Set() };
      // Check across clues.
      for (let i = 0; i < this.localGrid.length; i++) {
        for (let j = 0; j < this.localGrid[i].length; j++) {
          let cell = this.localGrid[i][j];
          if (
            cell &&
            !cell.isBlack &&
            (j === 0 || !this.localGrid[i][j - 1] || this.localGrid[i][j - 1].isBlack) &&
            cell.number
          ) {
            let wordCells = [];
            let col = j;
            while (
              col < this.localGrid[i].length &&
              this.localGrid[i][col] &&
              !this.localGrid[i][col].isBlack
            ) {
              wordCells.push({ row: i, col });
              col++;
            }
            const filled = wordCells.every(
              coord => this.localGrid[coord.row][coord.col].userValue !== ""
            );
            if (filled) {
              const correct = wordCells.every(
                coord =>
                  this.localGrid[coord.row][coord.col].userValue ===
                  this.localGrid[coord.row][coord.col].answer
              );
              if (correct) {
                solved.across.add(cell.number);
                for (const coord of wordCells) {
                  this.localGrid[coord.row][coord.col].solved = true;
                }
              }
            }
          }
        }
      }
      // Check down clues.
      for (let j = 0; j < this.localGrid[0].length; j++) {
        for (let i = 0; i < this.localGrid.length; i++) {
          let cell = this.localGrid[i][j];
          if (
            cell &&
            !cell.isBlack &&
            (i === 0 || !this.localGrid[i - 1][j] || this.localGrid[i - 1][j].isBlack) &&
            cell.number
          ) {
            let wordCells = [];
            let row = i;
            while (
              row < this.localGrid.length &&
              this.localGrid[row][j] &&
              !this.localGrid[row][j].isBlack
            ) {
              wordCells.push({ row, col: j });
              row++;
            }
            const filled = wordCells.every(
              coord => this.localGrid[coord.row][coord.col].userValue !== ""
            );
            if (filled) {
              const correct = wordCells.every(
                coord =>
                  this.localGrid[coord.row][coord.col].userValue ===
                  this.localGrid[coord.row][coord.col].answer
              );
              if (correct) {
                solved.down.add(cell.number);
                for (const coord of wordCells) {
                  this.localGrid[coord.row][coord.col].solved = true;
                }
              }
            }
          }
        }
      }
      this.$emit("solvedCluesUpdated", {
        across: Array.from(solved.across),
        down: Array.from(solved.down)
      });
    },
    isActiveCell(row, col) {
      return this.activeCells.some(coord => coord.row === row && coord.col === col);
    },
    handleInput(row, col, event) {
      let value = event.target.value.toUpperCase();
      this.localGrid[row][col].userValue = value;
      const idx = this.activeCells.findIndex(coord => coord.row === row && coord.col === col);
      if (idx !== -1 && idx < this.activeCells.length - 1) {
        const nextCoord = this.activeCells[idx + 1];
        this.$nextTick(() => {
          const nextEl = document.getElementById(`cell-${nextCoord.row}-${nextCoord.col}`);
          if (nextEl) nextEl.focus();
        });
      }
    },
    handleKeyDown(row, col, event) {
      if (event.key === "Backspace") {
        // Always clear current cell and move focus back
        event.preventDefault();
        this.localGrid[row][col].userValue = "";
        const idx = this.activeCells.findIndex(coord => coord.row === row && coord.col === col);
        if (idx > 0) {
          const prevCoord = this.activeCells[idx - 1];
          this.$nextTick(() => {
            const prevEl = document.getElementById(`cell-${prevCoord.row}-${prevCoord.col}`);
            if (prevEl) {
              prevEl.focus();
            }
          });
        }
      }
    },
    handleCellClick(row, col) {
      const cell = this.localGrid[row][col];
      if (!cell || cell.isBlack) return;
      const isAcrossStart = (col === 0 || !this.localGrid[row][col - 1] || this.localGrid[row][col - 1].isBlack);
      const isDownStart = (row === 0 || !this.localGrid[row - 1][col] || this.localGrid[row - 1][col].isBlack);
      if (cell.number) {
        if (isAcrossStart && isDownStart) {
          this.activateClueFromCell(row, col, "across", { row, col });
        } else if (isAcrossStart) {
          this.activateClueFromCell(row, col, "across", { row, col });
        } else if (isDownStart) {
          this.activateClueFromCell(row, col, "down", { row, col });
        } else {
          if (col > 0 && this.localGrid[row][col - 1] && !this.localGrid[row][col - 1].isBlack) {
            this.activateClueFromCell(row, col - 1, "across", { row, col });
          } else if (row > 0 && this.localGrid[row - 1][col] && !this.localGrid[row - 1][col].isBlack) {
            this.activateClueFromCell(row - 1, col, "down", { row, col });
          }
        }
      } else {
        let startCol = col;
        while (startCol > 0 && this.localGrid[row][startCol - 1] && !this.localGrid[row][startCol - 1].isBlack) {
          startCol--;
        }
        if (this.localGrid[row][startCol] && this.localGrid[row][startCol].number) {
          this.activateClueFromCell(row, startCol, "across", { row, col });
          return;
        }
        let startRow = row;
        while (startRow > 0 && this.localGrid[startRow - 1][col] && !this.localGrid[startRow - 1][col].isBlack) {
          startRow--;
        }
        if (this.localGrid[startRow][col] && this.localGrid[startRow][col].number) {
          this.activateClueFromCell(startRow, col, "down", { row, col });
          return;
        }
      }
    },
    activateClueFromCell(startRow, startCol, orientation, focusCoord = null) {
      this.activeCells = [];
      if (orientation === "across") {
        let col = startCol;
        while (
          col < this.localGrid[startRow].length &&
          this.localGrid[startRow][col] &&
          !this.localGrid[startRow][col].isBlack
        ) {
          this.activeCells.push({ row: startRow, col });
          col++;
        }
      } else if (orientation === "down") {
        let row = startRow;
        while (
          row < this.localGrid.length &&
          this.localGrid[row][startCol] &&
          !this.localGrid[row][startCol].isBlack
        ) {
          this.activeCells.push({ row, col: startCol });
          row++;
        }
      }
      this.selectedClue = {
        number: this.localGrid[startRow][startCol].number,
        orientation,
      };
      this.$nextTick(() => {
        const target = focusCoord || { row: startRow, col: startCol };
        const id = `cell-${target.row}-${target.col}`;
        const el = document.getElementById(id);
        if (el) el.focus();
      });
    },
    activateClue(clueNumber, orientation) {
      for (let i = 0; i < this.localGrid.length; i++) {
        for (let j = 0; j < this.localGrid[i].length; j++) {
          const cell = this.localGrid[i][j];
          if (cell && !cell.isBlack && cell.number === clueNumber) {
            this.activateClueFromCell(i, j, orientation, { row: i, col: j });
            return;
          }
        }
      }
    },
  },
  watch: {
    grid: {
      handler(newVal) {
        this.localGrid = JSON.parse(JSON.stringify(newVal));
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style scoped>
.crossword-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: Arial, sans-serif;
  padding: 20px;
}
.selected-clue {
  margin: 4px 0;
  font-weight: bold;
}
.grid {
  display: grid;
  grid-template-columns: repeat(var(--grid-cols), 40px);
  gap: 2px;
  margin: 10px 0;
}
.row {
  display: contents;
}
.cell {
  width: 40px;
  height: 40px;
  border: 1px solid #ccc;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}
.cell-block {
  background-color: #333;
  border: none;
}
.active {
  background-color: lightblue !important;
}
.solved {
  background-color: #77dd77 !important; /* pastel green */
}
.cell-number {
  position: absolute;
  top: 1px;
  left: 1px;
  font-size: 10px;
  color: #000;
}
input {
  width: 100%;
  height: 100%;
  text-align: center;
  font-size: 18px;
  border: none;
  outline: none;
  text-transform: uppercase;
  background: transparent;
}
button {
  margin-top: 10px;
  padding: 6px 12px;
  font-size: 16px;
}
.result-message {
  margin-top: 10px;
  font-weight: bold;
}

/* Media Query for Mobile */
@media (max-width: 600px) {
  .grid {
    grid-template-columns: repeat(var(--grid-cols), 28px);
  }
  .cell {
    width: 28px;
    height: 28px;
  }
  input {
    font-size: 12px;
  }
}
</style>